import { Input, Text } from '@/ui-kit'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui-kit/components/Dialog'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Check } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import type { SelectOption } from './api/dictionaries'

type LanguagePickerModalProps = {
  open: boolean
  onClose: () => void
  onSelect: (value: string) => void
  options: readonly SelectOption[]
  currentValue?: string
}

const splitWords = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .split(/[\s\-_/(),.]+/)
    .filter(Boolean)

const matchesByWordPrefix = (label: string, query: string) => {
  const queryWords = splitWords(query)
  if (!queryWords.length) return true
  const labelWords = splitWords(label)
  return queryWords.every((qw) => labelWords.some((lw) => lw.startsWith(qw)))
}

export const LanguagePickerModal = ({ open, onClose, onSelect, options, currentValue }: LanguagePickerModalProps) => {
  const { t } = useLingui()
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim()
    if (!q) return options
    return options.filter((opt) => matchesByWordPrefix(opt.label, q))
  }, [options, searchQuery])

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('')
      onClose()
    }
  }

  const handleSelect = (value: string) => {
    setSearchQuery('')
    onSelect(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex! max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Select Language</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 mt-6">
          <Input
            ref={searchInputRef}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t`Search language...`}
          />
        </div>

        <div className="relative flex h-full overflow-hidden">
          <div className="scrollbar min-h-0 flex-1 overflow-y-auto p-2 pb-6">
            {filteredOptions.map((opt) => {
              const isSelected = opt.value === currentValue
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={clsx(
                    'text-body-2 flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg py-3 pr-3 pl-4 text-left transition-colors outline-none',
                    'hover:bg-gray-100/60',
                    isSelected && 'text-orange bg-gray-100/60'
                  )}
                >
                  <span className="line-clamp-2">{opt.label}</span>
                  {isSelected && <Check className="text-orange size-4 shrink-0" />}
                </button>
              )
            })}

            {filteredOptions.length === 0 && (
              <div className="text-gray-80 text-body3-regular py-6 text-center">
                <Text type="body2-regular">
                  <Trans>No matches</Trans>
                </Text>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
